import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CardFoot, CardText } from '../BlackCardPlayer/BlackCardContainer';
import { Check, ErrorText, Pen, Trophy } from '../icons';
import { t } from '../../i18n';
import { Bdi, textDir } from '../../i18n/direction';

const blankName = i => [t('game.blank1'), t('game.blank2'), t('game.blank3')][i]
const WRITTEN = Symbol('written') // the blank card's key among the selected card texts
const MAX_WRITTEN = 41
const FIELD = { display: 'block', width: '100%', border: 0, padding: 0, background: 'transparent', font: 'inherit', color: 'inherit', resize: 'none', outline: 'none' }
// An answer card's text reads in its own direction; the badge and the pen stay where the screen puts them.
const Words = ({ text }) => <span dir={textDir(text)} style={{ display: 'block' }}>{text}</span>
const BlankFace = () => <><span className="pen" aria-hidden="true"><Pen /></span><span>{t('game.writeYourOwn')}</span></>

function WhiteCardPlayer({ whiteCards, hasBlank, answerError, onSendAnswer, winner, roundResult, points, requiredAnswers, questionCard }) {
  const [selectedCards, setSelectedCards] = useState([])
  const [sentAnswers, setSentAnswers] = useState(null)
  const [handAtSend, setHandAtSend] = useState([])
  const [written, setWritten] = useState('')
  const [writing, setWriting] = useState(false)

  useEffect(() => {
    setSentAnswers(null);
    // an unused blank card does not carry over to the next round
    setWritten('')
    setWriting(false)
    setSelectedCards(s => s.filter(k => k !== WRITTEN))
  }, [questionCard, setSentAnswers])

  useEffect(() => {
    if (answerError && sentAnswers) { // refused: back to the picked cards, the written text kept for editing
      setSelectedCards(sentAnswers)
      setSentAnswers(null)
    }
  }, [answerError, sentAnswers])

  const asCards = keys => keys.map(k => k === WRITTEN ? { text: written, written: true } : { text: k })
  const full = selectedCards.length === requiredAnswers
  const canSend = full && (!selectedCards.includes(WRITTEN) || written.trim() !== '')

  const handleSendAnswerClick = () => {
    if (!canSend) return
    onSendAnswer(selectedCards.map(k => k === WRITTEN ? { text: written.trim(), written: true } : whiteCards.find(c => c.text === k)))
    setHandAtSend(whiteCards.map(c => c.text))
    setSentAnswers(selectedCards)
    setSelectedCards([])
  }

  const onCardClicked = (card) => {
    if (selectedCards.indexOf(card) === -1 && selectedCards.length < requiredAnswers) {
      setSelectedCards([...selectedCards, card])
    } else {
      setSelectedCards(selectedCards.filter(c => c !== card))
    }
  }

  const stats = (
    <div className="stats" style={roundResult ? { justifyContent: 'center' } : undefined}>
      <span>{t('game.points')} <b>{points}</b></span><span className="sep">|</span><span>{t('game.inHand')} <b>{whiteCards.length + (hasBlank ? 1 : 0)}</b></span>
      {!roundResult && <><span className="sep">|</span><span>{t('game.picked')} <b style={{ direction: 'ltr' }}>{t('common.counter', { n: selectedCards.length, max: questionCard ? requiredAnswers : '–' })}</b></span></>}
    </div>
  )

  if (winner) {
    return (
      <div style={{ flex: 1, background: '#4b1d52', color: '#ffffff', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28, padding: '24px 20px 40px' }}>
        <svg className="burst" width="390" height="300" viewBox="0 0 390 300" aria-hidden="true" style={{ position: 'absolute', top: 24, left: 0 }}>
          <rect x="40" y="40" width="44" height="14" rx="7" fill="#7fe0b5" transform="rotate(-24 62 47)"></rect>
          <rect x="300" y="30" width="56" height="14" rx="7" fill="#ee5a4a" transform="rotate(18 328 37)"></rect>
          <rect x="20" y="170" width="36" height="14" rx="7" fill="#ee5a4a" transform="rotate(30 38 177)"></rect>
          <rect x="320" y="190" width="48" height="14" rx="7" fill="#7fe0b5" transform="rotate(-30 344 197)"></rect>
          <circle cx="120" cy="22" r="8" fill="#fff6e0"></circle>
          <circle cx="262" cy="120" r="10" fill="#7fe0b5"></circle>
          <circle cx="64" cy="112" r="6" fill="#fff6e0"></circle>
          <circle cx="350" cy="122" r="6" fill="#fff6e0"></circle>
          <rect x="180" y="6" width="30" height="14" rx="7" fill="#fff6e0" transform="rotate(10 195 13)"></rect>
        </svg>

        <h1 className="win-in you-won" style={{ margin: 0, color: '#7fe0b5', transform: 'rotate(-4deg)', position: 'relative' }}>{t('game.youWon')}</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <span style={{ font: '500 18px var(--sans)' }}>{t('game.points')} <b style={{ fontSize: 24 }}>{points}</b></span>
          <span style={{ background: '#7fe0b5', color: '#24102a', border: '2px solid #24102a', borderRadius: 999, padding: '4px 12px', font: '700 16px var(--sans)', direction: 'ltr' }}>+{1}</span>
        </div>

        <section className="bc" style={{ outline: '4px solid #7fe0b5', outlineOffset: 4, transform: 'rotate(2deg)', position: 'relative', borderColor: '#fff6e0' }}>
          <CardText question={questionCard} answers={asCards(sentAnswers ?? [])} />
          <CardFoot />
        </section>
      </div>
    )
  }

  if (roundResult) {
    return (
      <div className="body" style={{ justifyContent: 'center', alignItems: 'center', gap: 28, padding: '24px 20px 64px' }}>
        <span style={{ width: 72, height: 72, borderRadius: '50%', background: '#7fe0b5', border: '2px solid #24102a', boxShadow: '0 4px 0 #24102a', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }} aria-hidden="true">
          <Trophy size={38} color="#24102a" width={2} />
        </span>

        <h1 className="h-l" style={{ textAlign: 'center', maxWidth: 340 }}>{t('game.roundWinner')} <span style={{ background: '#ee5a4a', color: '#24102a', padding: '0 10px', borderRadius: 10, boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}><Bdi>{roundResult.name}</Bdi></span></h1>

        <section className="bc" style={{ outline: '4px solid #7fe0b5', outlineOffset: 4 }}>
          <CardText question={questionCard} answers={roundResult.cards} />
          <CardFoot />
        </section>

        {stats}
      </div>
    )
  }

  if (!questionCard) {
    return (
      <>
        <div className="body with-bar">
          {stats}
          <section aria-busy="true" aria-label={t('game.questionComing')} style={{ border: '2px dashed #4b1d52', borderRadius: 16, padding: '22px 18px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 132, background: '#f3e6d6' }}>
            <span style={{ display: 'block', height: 16, width: '86%', borderRadius: 8, background: '#d9c3d8' }}></span>
            <span style={{ display: 'block', height: 16, width: '64%', borderRadius: 8, background: '#d9c3d8' }}></span>
            <span style={{ display: 'block', height: 16, width: '40%', borderRadius: 8, background: '#d9c3d8' }}></span>
          </section>
          <div className="hand">
            {hasBlank && <button className="wc wc-blank"><BlankFace /></button>}
            {whiteCards.map(c => <button key={c.text} className="wc"><Words text={c.text} /></button>)}
          </div>
        </div>
        <div className="bar">
          <button className="btn btn-disabled btn-block" disabled>{t('game.send')}</button>
        </div>
      </>
    )
  }

  if (sentAnswers) {
    return (
      <>
        <div className="body with-bar">
          {stats}
          <section className="bc" aria-label={t('game.sentCard')} style={{ position: 'relative', marginTop: 6 }}>
            <span className="stamp" aria-hidden="true"><Check size={30} /></span>
            <CardText question={questionCard} answers={asCards(sentAnswers)} />
            <CardFoot pick={requiredAnswers} />
          </section>
          <div className="hand" style={{ marginTop: 4, opacity: .55 }} aria-disabled="true">
            {sentAnswers.includes(WRITTEN)
              ? <span className="wc wc-spent" role="img" aria-label={t('game.blankUsed')}><Pen size={28} width={2} crossed /></span>
              : hasBlank && <button className="wc wc-blank" disabled><BlankFace /></button>}
            {whiteCards.map(c => (
              <button key={c.text} className="wc" disabled
                style={handAtSend.includes(c.text) ? { boxShadow: 'none' } : { boxShadow: 'none', borderColor: '#1f7a52', borderWidth: 3 }}><Words text={c.text} /></button>
            ))}
          </div>
        </div>
        <div className="bar">
          <button className="btn btn-disabled btn-block" disabled>
            <Check size={20} />
            {t('game.send')}
          </button>
        </div>
      </>
    )
  }

  const blackCard = (
    <section className="bc" aria-label={t('game.questionCard')}>
      <CardText question={questionCard} answers={asCards(selectedCards)} />
      <CardFoot pick={requiredAnswers} />
    </section>
  )

  if (writing) {
    const save = () => {
      setWritten(written.trim())
      if (!written.trim()) setSelectedCards(s => s.filter(k => k !== WRITTEN))
      setWriting(false)
    }
    return (
      <div className="body" style={{ gap: 14 }}>
        {stats}
        {blackCard}
        <label className="wc wc-writing" style={{ display: 'block' }}>
          <textarea autoFocus rows={3} maxLength={MAX_WRITTEN} aria-label={t('game.writeYourOwn')} style={FIELD} dir={textDir(written)}
            value={written} onChange={e => setWritten(e.target.value)} />
          <span className="wc-count">{t('common.counter', { n: written.length, max: MAX_WRITTEN })}</span>
        </label>
        <button className="btn btn-primary btn-block" onClick={save}>{t('game.saveWritten')}</button>
      </div>
    )
  }

  const writtenIndex = selectedCards.indexOf(WRITTEN)
  const blankCard = !hasBlank ? null
    : !written ? <button className="wc wc-blank" aria-label={t('game.blankCard')} onClick={() => setWriting(true)}><BlankFace /></button>
    : answerError && writtenIndex > -1
      ? <label className="wc wc-writing wc-err" style={{ display: 'block' }}>
        <span className="badge" aria-hidden="true">{writtenIndex + 1}</span>
        <textarea rows={3} maxLength={MAX_WRITTEN} aria-invalid="true" aria-describedby="dup-err" aria-label={t('game.yourCard')} style={FIELD} dir={textDir(written)}
          value={written} onChange={e => setWritten(e.target.value)} />
        <span className="wc-count">{t('common.counter', { n: written.length, max: MAX_WRITTEN })}</span>
      </label>
    : writtenIndex > -1
      ? <button className="wc wc-written wc-sel" aria-pressed="true" aria-label={t('game.yourCardInBlank', { text: written, blank: blankName(writtenIndex) })} onClick={() => onCardClicked(WRITTEN)}>
        <span className="badge" aria-hidden="true">{writtenIndex + 1}</span><Words text={written} /><span className="pen-tag" aria-hidden="true"><Pen size={14} width={2.4} /></span>
      </button>
      : <button className={`wc wc-written${full ? ' wc-off' : ''}`} aria-pressed="false" aria-disabled={full || undefined} aria-label={t('game.yourCardText', { text: written })} onClick={() => onCardClicked(WRITTEN)}>
        <Words text={written} /><span className="pen-tag" aria-hidden="true"><Pen size={14} width={2.4} /></span>
      </button>

  return (
    <>
      <div className={answerError ? 'body' : 'body with-bar'} style={answerError ? { paddingBottom: 170 } : undefined}>
        {stats}
        {blackCard}
        <div className="hand" style={{ marginTop: 4 }}>
          {blankCard}
          {whiteCards.map(c => {
            const index = selectedCards.indexOf(c.text)
            return index > -1
              ? <button key={c.text} className="wc wc-sel" aria-pressed="true" aria-label={t('game.cardInBlank', { text: c.text, blank: blankName(index) })} onClick={() => onCardClicked(c.text)}>
                <span className="badge" aria-hidden="true">{index + 1}</span><Words text={c.text} />
              </button>
              : <button key={c.text} className={`wc${full ? ' wc-off' : ''}`} aria-pressed="false" aria-disabled={full || undefined} onClick={() => onCardClicked(c.text)}><Words text={c.text} /></button>
          })}
        </div>
      </div>
      <div className="bar">
        {answerError && <ErrorText id="dup-err">{answerError === 'duplicate_card' ? t('error.duplicateCard') : t('error.general')}</ErrorText>}
        {canSend
          ? <button className="btn btn-primary btn-block" onClick={handleSendAnswerClick}>{t('game.send')}</button>
          : <button className="btn btn-disabled btn-block" disabled>{t('game.send')}</button>}
      </div>
    </>
  )
}

WhiteCardPlayer.propTypes = {
  whiteCards: PropTypes.arrayOf(PropTypes.shape({ 'text': PropTypes.string, 'category': PropTypes.string })).isRequired,
  hasBlank: PropTypes.bool,
  answerError: PropTypes.string,
  onSendAnswer: PropTypes.func,
  winner: PropTypes.bool,
  roundResult: PropTypes.shape({ name: PropTypes.string, cards: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, written: PropTypes.bool })) }),
  points: PropTypes.number,
  requiredAnswers: PropTypes.number,
}

WhiteCardPlayer.defaultProps = {
  onSendAnswer: () => { },
  winner: false,
  points: 0
}

export default WhiteCardPlayer
